// software.js
//
// Structured page content for the Software_Page, which presents Civillo —
// ThinkSpatial's web-mapping platform for construction projects. Copy is drawn
// from the ThinkSpatial Capability Statement and lives here rather than in the
// component template so editorial changes need no redesign (Requirement 11.2).
//
// `SoftwarePage.vue` imports this module.

/**
 * The Software_Page content model.
 *
 * @typedef {Object} SoftwareContent
 * @property {Object} hero      - product name, lead, and supporting copy
 * @property {Object[]} stats   - traction figures shown beneath the hero
 * @property {Object} problem   - the need Civillo addresses
 * @property {Object[]} features - what the platform provides
 * @property {Object} permits   - the Civillo Permit Module
 * @property {Object} closingCta - CTA to Contact
 * @property {import('./seo').PageSeo} seo - per-page SEO metadata
 */

export const software = {
  hero: {
    eyebrow: 'Our software',
    name: 'Civillo',
    lead: 'A web-mapping platform built specifically for construction projects.',
    body:
      'Civillo provides a single point of access to design data, existing conditions information, as-built data, photos, and reports — so architects, engineers, contractors, and managers all work from the same up-to-date information.'
  },

  // Traction figures, as stated in the capability statement.
  stats: [
    { value: 'Hundreds', label: 'of projects using Civillo' },
    { value: 'Thousands', label: 'of users across Australia' },
    { value: 'Multi-discipline', label: 'design, construction, and management teams' }
  ],

  problem: {
    heading: 'Why we built it',
    body: [
      'Construction sites are extremely dynamic and complex environments, with constant changes in the state of infrastructure both above and below ground. In these environments it is critical to know the location and status of existing, newly constructed, and planned infrastructure.',
      'That information also has to be easy to access and available in a timely fashion. Civillo was developed to address exactly that need.'
    ]
  },

  features: {
    heading: 'What Civillo brings together',
    items: [
      {
        title: 'Design data',
        body: 'Current design information, available to every discipline in one place.'
      },
      {
        title: 'Existing conditions',
        body: 'Feature and underground services data captured in the field, kept current.'
      },
      {
        title: 'As-built survey',
        body: 'As-built data and reports are available immediately after processing.'
      },
      {
        title: 'Photos and reports',
        body: 'Documents, spreadsheets, and PDFs attached in their spatial context.'
      }
    ]
  },

  permits: {
    heading: 'Civillo Permit Module',
    body: [
      'The Civillo Permit Module offers a flexible template builder and the ability to digitise the entire construction permit lifecycle, while leveraging the spatial data already hosted in Civillo.',
      'Permits are managed in the same user-friendly interface as the rest of your project data, keeping approvals tied to the locations they apply to.'
    ]
  },

  closingCta: {
    heading: 'See Civillo on your project',
    body:
      'Get in touch for a walkthrough of the platform and how it could work alongside your delivery team.',
    cta: { label: 'Request a walkthrough', to: '/contact' }
  },

  /** @type {import('./seo').PageSeo} */
  seo: {
    title: 'Civillo — Construction web mapping | ThinkSpatial',
    description:
      'Civillo is ThinkSpatial\u2019s web-mapping platform for construction projects, bringing design data, existing conditions, as-built survey, photos, reports, and digital permits into a single point of access.'
  }
}

export default software
